import { EMPTY_DISPLAY_VALUE } from '../../../constants/formatting';
import { Nullable } from '../../../types/common.types';
import isFiniteNumber from '../isFiniteNumber';

import addThousandsSeparator from './addThousandsSeparator';
import removeTrailingZeros from './removeTrailingZeros';
import roundToExactNumberOfDecimals from './roundToExactNumberOfDecimals';

export type FormatNumberOptions = {
  /**
   * how many decimals to display
   * @default 2
   */
  decimals?: number;

  /**
   * fallback to return if value is nullish
   * @default `-`
   * */
  fallback?: string;

  /**
   * You can use this option if you wanna show `1639.00` instead of `1,639.00` for example
   * @default false
   */
  omitThousandSeparator?: boolean;

  /**
   * You can use this option if you wanna show `420.6` instead of `420.60`
   * @default false
   */
  omitTrailingZeros?: boolean;
};

/**
 * Formats number into a format that is easily readable for users
 *
 * @example
 * formatNumber(12645.2846)
 * // Returns `12,645.28`
 */
const formatNumber = (
  value: Nullable<number>,
  {
    decimals = 2,
    omitThousandSeparator = false,
    omitTrailingZeros = false,
    fallback = EMPTY_DISPLAY_VALUE,
  }: FormatNumberOptions = {},
): string => {
  if (!isFiniteNumber(value)) return fallback;

  const formattedNumber = roundToExactNumberOfDecimals(value, decimals);

  const withFormattedSeparator = omitThousandSeparator
    ? formattedNumber
    : addThousandsSeparator(formattedNumber);

  return omitTrailingZeros
    ? removeTrailingZeros(withFormattedSeparator)
    : withFormattedSeparator;
};

export default formatNumber;
